import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import PublicLayout from "@/layouts/PublicLayout";
import ScrollToTop from "@/components/ScrollToTop";
import { Toaster } from "@/components/ds";

import Landing from "@/pages/public/Landing";
import Pricing from "@/pages/public/Pricing";
import GetStarted from "@/pages/public/GetStarted";
import PurchaseConfirmation from "@/pages/public/PurchaseConfirmation";
import Partners from "@/pages/public/Partners";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import AppointmentManage from "@/pages/public/AppointmentManage";
import CustomerPortal from "@/pages/public/CustomerPortal";
import { ChatWidget } from "@/components/ChatWidget";
import Login from "@/pages/public/Login";
import MfaChallenge from "@/pages/public/MfaChallenge";
import InternalLayout from "@/layouts/InternalLayout";
import InternalOverview from "@/pages/internal/Overview";
import InternalAuditLogs from "@/pages/internal/AuditLogs";
import InternalActivate from "@/pages/internal/Activate";
import OnboardingAccounts from "@/pages/internal/OnboardingAccounts";
import Infrastructure from "@/pages/internal/Infrastructure";
import Telephony from "@/pages/internal/Telephony";
import CustomerActivation from "@/pages/public/CustomerActivation";
import SalesDashboard from "@/pages/internal/sales/SalesDashboard";
import Representatives from "@/pages/internal/sales/Representatives";
import RepDetail from "@/pages/internal/sales/RepDetail";
import SalesLedger from "@/pages/internal/sales/Ledger";
import Payouts from "@/pages/internal/sales/Payouts";
import GrowthMix from "@/pages/internal/sales/GrowthMix";
import MyPerformance from "@/pages/internal/sales/MyPerformance";
import ExecutiveLayout from "@/layouts/ExecutiveLayout";
import { ExecOverview, ExecSalesTeam, ExecRepDetail, ExecBusinesses, ExecAnalytics, ExecCompensation, ExecBonuses } from "@/pages/executive/Executive";

function InternalRoute({ children }: { children: React.ReactNode }) {
  return <InternalLayout>{children}</InternalLayout>;
}
import MfaSetup from "@/pages/app/MfaSetup";
import ForgotPassword from "@/pages/public/ForgotPassword";
import ResetPassword from "@/pages/public/ResetPassword";
import VerifyEmail from "@/pages/public/VerifyEmail";
import ModulePage from "@/pages/app/ModulePage";
import Settings from "@/pages/app/Settings";
import Dashboard from "@/pages/app/Dashboard";
import Knowledge from "@/pages/app/Knowledge";
import Services from "@/pages/app/Services";
import Demo from "@/pages/app/Demo";
import Leads from "@/pages/app/Leads";
import Scheduling from "@/pages/app/Scheduling";
import Integrations from "@/pages/app/Integrations";
import Inbox from "@/pages/app/Inbox";
import Receptionist from "@/pages/app/Receptionist";
import Simulator from "@/pages/app/Simulator";
import SmsAutopilot from "@/pages/app/SmsAutopilot";
import Voice from "@/pages/app/Voice";
import Notifications from "@/pages/app/Notifications";
import Billing from "@/pages/app/Billing";
import Analytics from "@/pages/app/Analytics";
import Opportunity from "@/pages/app/Opportunity";
import LostLeads from "@/pages/app/LostLeads";
import OptimizationCenter from "@/pages/app/OptimizationCenter";
import Recovery from "@/pages/app/Recovery";
import NoShowRecovery from "@/pages/app/NoShowRecovery";
import Campaigns from "@/pages/app/Campaigns";
import OutboundCalls from "@/pages/app/OutboundCalls";
import Estimates from "@/pages/app/Estimates";
import Reviews from "@/pages/app/Reviews";
import Memberships from "@/pages/app/Memberships";
import Seasonal from "@/pages/app/Seasonal";
import Locations from "@/pages/app/Locations";
import ReviewPage from "@/pages/public/ReviewPage";
import Onboarding from "@/pages/app/Onboarding";
import AgencyPartners from "@/pages/app/admin/AgencyPartners";
import Languages from "@/pages/app/Languages";
import Team from "@/pages/app/Team";
import { NAV_ITEMS } from "@/config/navigation";

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Public */}
          <Route path="/" element={<PublicLayout><Landing /><ChatWidget /></PublicLayout>} />
          <Route path="/pricing" element={<PublicLayout><Pricing /><ChatWidget /></PublicLayout>} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/partners" element={<PublicLayout><Partners /><ChatWidget /></PublicLayout>} />
          <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
          <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />
          <Route path="/welcome" element={<PurchaseConfirmation />} />
          <Route path="/login" element={<Login />} />
          <Route path="/mfa" element={<MfaChallenge />} />
          <Route path="/internal/activate" element={<InternalActivate />} />
          <Route path="/activate/:token" element={<CustomerActivation />} />
          <Route path="/internal" element={<InternalRoute><InternalOverview /></InternalRoute>} />
          <Route path="/internal/onboarding-accounts" element={<InternalRoute><OnboardingAccounts /></InternalRoute>} />
          <Route path="/internal/infrastructure" element={<InternalRoute><Infrastructure /></InternalRoute>} />
          <Route path="/internal/telephony" element={<InternalRoute><Telephony /></InternalRoute>} />
          <Route path="/internal/audit-logs" element={<InternalRoute><InternalAuditLogs /></InternalRoute>} />
          <Route path="/internal/sales" element={<InternalRoute><SalesDashboard /></InternalRoute>} />
          <Route path="/internal/sales/reps" element={<InternalRoute><Representatives /></InternalRoute>} />
          <Route path="/internal/sales/reps/:userId" element={<InternalRoute><RepDetail /></InternalRoute>} />
          <Route path="/internal/sales/ledger" element={<InternalRoute><SalesLedger /></InternalRoute>} />
          <Route path="/internal/sales/payouts" element={<InternalRoute><Payouts /></InternalRoute>} />
          <Route path="/internal/sales/growth-mix" element={<InternalRoute><GrowthMix /></InternalRoute>} />
          <Route path="/internal/sales/me" element={<InternalRoute><MyPerformance /></InternalRoute>} />
          <Route path="/executive" element={<ExecutiveLayout><ExecOverview /></ExecutiveLayout>} />
          <Route path="/executive/sales-team" element={<ExecutiveLayout><ExecSalesTeam /></ExecutiveLayout>} />
          <Route path="/executive/bonuses" element={<ExecutiveLayout><ExecBonuses /></ExecutiveLayout>} />
          <Route path="/executive/reps/:userId" element={<ExecutiveLayout><ExecRepDetail /></ExecutiveLayout>} />
          <Route path="/executive/businesses" element={<ExecutiveLayout><ExecBusinesses /></ExecutiveLayout>} />
          <Route path="/executive/analytics" element={<ExecutiveLayout><ExecAnalytics /></ExecutiveLayout>} />
          <Route path="/executive/compensation" element={<ExecutiveLayout><ExecCompensation /></ExecutiveLayout>} />
          <Route path="/mfa-setup" element={<MfaSetup />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/appt/:token" element={<AppointmentManage />} />
          <Route path="/review/:token" element={<ReviewPage />} />
          <Route path="/portal/:token" element={<CustomerPortal />} />

          {/* Authenticated */}
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/app/admin/agency" element={<ProtectedRoute><AgencyPartners /></ProtectedRoute>} />
          <Route path="/app/languages" element={<ProtectedRoute><Languages /></ProtectedRoute>} />
          {NAV_ITEMS.map((item) => (
            <Route
              key={item.key}
              path={item.path}
              element={
                <ProtectedRoute>
                  {item.key === "settings"
                    ? <Settings />
                    : item.key === "team"
                      ? <Team />
                    : item.key === "dashboard"
                      ? <Dashboard />
                      : item.key === "leads"
                        ? <Leads />
                        : item.key === "inbox"
                          ? <Inbox />
                          : item.key === "sms"
                            ? <SmsAutopilot />
                          : item.key === "voice"
                            ? <Voice />
                          : item.key === "notifications"
                            ? <Notifications />
                          : item.key === "billing"
                            ? <Billing />
                          : item.key === "analytics"
                            ? <Analytics />
                          : item.key === "opportunity"
                            ? <Opportunity />
                          : item.key === "lost-leads"
                            ? <LostLeads />
                          : item.key === "optimization"
                            ? <OptimizationCenter />
                          : item.key === "recovery"
                            ? <Recovery />
                          : item.key === "noshow"
                            ? <NoShowRecovery />
                          : item.key === "campaigns"
                            ? <Campaigns />
                          : item.key === "seasonal"
                            ? <Seasonal />
                          : item.key === "locations"
                            ? <Locations />
                          : item.key === "outbound"
                            ? <OutboundCalls />
                          : item.key === "estimates"
                            ? <Estimates />
                          : item.key === "reviews"
                            ? <Reviews />
                          : item.key === "memberships"
                            ? <Memberships />
                          : item.key === "appointments"
                          ? <Scheduling />
                          : item.key === "integrations"
                          ? <Integrations />
                          : item.key === "knowledge"
                          ? <Knowledge />
                          : item.key === "services"
                            ? <Services />
                            : item.key === "receptionist"
                              ? <Receptionist />
                            : item.key === "simulator"
                              ? <Simulator />
                            : item.key === "demo"
                              ? <Demo />
                              : <ModulePage title={item.label} description={item.description} />}
                </ProtectedRoute>
              }
            />
          ))}
          <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
